/**
 * AvatarUploader - carica/mostra l'avatar utente (Fase 2).
 *
 * Segue il pattern del LogoUploader azienda: l'immagine e' servita da un GET
 * autenticato (Authorization header), quindi va scaricata come blob e mostrata
 * via ObjectURL (non si puo' usare <img src> diretto). Upload multipart verso
 * POST /api/v1/utenti/{id}/avatar; il backend risponde con l'utente aggiornato
 * (avatar_documento_id).
 */
import React, { useEffect, useRef, useState } from 'react';

interface AvatarUploaderProps {
  token: string;
  utenteId: string;
  /** avatar_documento_id corrente (null se assente): triggera il refetch della preview. */
  avatarDocumentoId: string | null;
  nome: string;
  cognome: string;
  /** Chiamato dopo un upload riuscito con il nuovo avatar_documento_id. */
  onChange: (nuovoAvatarDocumentoId: string | null) => void;
  hint?: string;
  uploadLabel: string;
}

function iniziali(nome: string, cognome: string): string {
  const a = (nome || '').trim().charAt(0);
  const b = (cognome || '').trim().charAt(0);
  return (a + b).toUpperCase() || '?';
}

const AvatarUploader: React.FC<AvatarUploaderProps> = ({
  token, utenteId, avatarDocumentoId, nome, cognome, onChange, hint, uploadLabel,
}) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!avatarDocumentoId) {
      setPreviewSrc(null);
      return;
    }
    let revoked = false;
    let createdUrl: string | null = null;
    fetch(`/api/v1/utenti/${utenteId}/avatar`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Anteprima non disponibile (${res.status})`);
        const blob = await res.blob();
        if (revoked) return;
        createdUrl = URL.createObjectURL(blob);
        setPreviewSrc(createdUrl);
      })
      .catch(() => {
        if (!revoked) setPreviewSrc(null);
      });
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [avatarDocumentoId, utenteId, token]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setErr('Formato non supportato (PNG/JPEG)');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErr('File troppo grande (max 2MB)');
      return;
    }
    setUploading(true);
    setErr(null);
    const localUrl = URL.createObjectURL(file);
    setPreviewSrc(localUrl);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/v1/utenti/${utenteId}/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Errore ${res.status}`);
      }
      const j = await res.json();
      onChange(j.avatar_documento_id ?? null);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Errore upload');
      URL.revokeObjectURL(localUrl);
      setPreviewSrc(null);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1B3A6B',
          color: '#FFFFFF',
          fontSize: 24,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {previewSrc ? (
          <img src={previewSrc} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          iniziali(nome, cognome)
        )}
      </div>
      <div>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg"
          onChange={handleFile}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          style={{
            padding: '6px 14px',
            fontSize: 13,
            borderRadius: 6,
            border: '1px solid var(--color-border)',
            backgroundColor: 'var(--color-background)',
            color: 'var(--color-foreground)',
            cursor: uploading ? 'not-allowed' : 'pointer',
          }}
        >
          {uploading ? '...' : uploadLabel}
        </button>
        {hint && (
          <p style={{ fontSize: 12, color: 'var(--color-muted-foreground)', marginTop: 6 }}>{hint}</p>
        )}
        {err && (
          <p style={{ fontSize: 12, color: 'var(--color-destructive, #DC2626)', marginTop: 6 }}>{err}</p>
        )}
      </div>
    </div>
  );
};

export default AvatarUploader;
